import React, { useState } from "react";
import { Form, FormControl, FormGroup, Image, Spinner } from "react-bootstrap";
import { library } from "@fortawesome/fontawesome-svg-core";
import Select from "react-select";
import Modal from "react-bootstrap/Modal";
import {
  faFileDownload,
  faPlusCircle,
} from "@fortawesome/free-solid-svg-icons";
import { ApplicationState } from "../../../../../../store";
import { bindActionCreators, Dispatch } from "redux";
import { connect } from "react-redux";
import { School } from "../../../../../../store/onboarding/types";
import {
  hideEditProfileModal,
  updateProfile,
} from "../../../../../../store/auth/actions";
import { getSchools } from "../../../../../../store/onboarding/actions";
import { ValueType } from "react-select/src/types";
import {
  UserAssignmentRoleDisplayedNames,
  UserInfo,
} from "../../../../../../store/auth/types";
import { FormattedMessage } from "react-intl";
import { useLoading } from "../../../../../../utils/hooks/useLoading";
import PictureReader from "../../../../file-reader/PictureReader";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPen, faUserCheck } from "@fortawesome/free-solid-svg-icons";
import { escapeSpecialCharacter } from "../../../../../../utils/NamesUtils";
import useUserRole from "../../../../../../utils/hooks/useUserRole";

library.add(faFileDownload, faPlusCircle);

type PropsFromState = {
  userInfo?: UserInfo;
  schools: Array<School>;
  showModal: boolean;
  isLoading: {
    updateProfile: boolean;
    getSchoolsList: boolean;
    uploadUserProfileImage: boolean;
  };
  errors: {
    getSchoolsList?: string;
    updateProfile?: string;
  };
};

type DispatchProps = {
  hideEditProfileModal: () => any;
  getSchools: () => any;
  updateProfile: (userInfo: UserInfo) => any;
};

type Props = PropsFromState & DispatchProps;

const EditProfileModal: React.FC<Props> = (props) => {
  const { showModal, schools, userInfo, isLoading, errors } = props;
  const { isDistrictOwner } = useUserRole();

  const [firstName, setFirstName] = useState<string>("");
  const [lastName, setLastName] = useState<string>("");
  const [email, setEmail] = useState<string>("");
  const [chosenSchools, setChosenSchools] = useState<number[]>([]);
  const [customRoleTitle, setCustomRoleTitle] = useState<string | undefined>(
    undefined
  );

  useLoading(showModal && isLoading.updateProfile, errors.updateProfile);
  useLoading(showModal && isLoading.getSchoolsList, errors.getSchoolsList);

  const handleShowModal = () => {
    props.getSchools();
    if (userInfo) {
      setFirstName(userInfo.first_name);
      setLastName(userInfo.last_name);
      setEmail(userInfo.email);
      setChosenSchools(
        userInfo.profile.current_assignment
          ? userInfo.profile.current_assignment.schools.map(
              (school) => school.id
            )
          : []
      );
      setCustomRoleTitle(userInfo?.profile.custom_role_title);
    }
  };

  const handleHideModal = () => {
    props.hideEditProfileModal();
  };

  const handleFirstNameChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { value } = event.target;
    const sanitizedValue = escapeSpecialCharacter(value);
    setFirstName(sanitizedValue);
  };

  const handleLastNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    const sanitizedValue = escapeSpecialCharacter(value);
    setLastName(sanitizedValue);
  };

  const handleEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setEmail(value);
  };

  const handleCustomRoleTitleChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { value } = event.target;
    setCustomRoleTitle(value);
  };

  const handleSelectedSchoolChange = (values: ValueType<School, true>) => {
    setChosenSchools(values ? (values as Array<School>).map((t) => t.id!) : []);
  };

  const handleSubmit = (event: React.FormEvent<any>) => {
    event.preventDefault();
    if (!userInfo) return;
    const updatedUserInfo: UserInfo = {
      ...userInfo,
      first_name: firstName,
      last_name: lastName,
      //email: email, RC-1907
      profile: {
        ...userInfo.profile,
        current_assignment: {
          ...userInfo.profile.current_assignment!,
          schools: props.schools.filter((school) =>
            chosenSchools.includes(school.id)
          ),
        },
        custom_role_title: customRoleTitle,
      },
      available_grades: userInfo.available_grades,
    };
    props.updateProfile(updatedUserInfo);
  };

  return (
    <Modal
      show={showModal}
      onHide={handleHideModal}
      onShow={handleShowModal}
      animation={false}
      centered
      backdropClassName={"customDarkModalBackdrop in"}
    >
      <Modal.Header
        closeButton
        className={"purpleModalHeader"}
      >
        <Modal.Title>
          <FormattedMessage id="app.dashboard.profile.modal.editingInfo" />
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="d-flex flex-column align-items-center justify-content-between">
          <div className="d-flex">
            <Image
              style={{
                objectFit: "cover",
                objectPosition: "top center",
                border: "1px solid rgb(97, 97, 97)",
              }}
              src={userInfo && userInfo.profile.profile_image_url}
              width={100}
              height={100}
              roundedCircle
            />
            {isLoading.uploadUserProfileImage && (
              <div
                style={{
                  width: 100,
                  height: 100,
                }}
                className="loadImageSpinnerContainer rounded-circle"
              >
                <Spinner animation="border" />
              </div>
            )}
          </div>
          <OverlayTrigger
            placement="right"
            overlay={
              <Tooltip id="edit-profile-pic" className="customInfoTooltip" style={{maxWidth:205}}>
                Upload a JPG, JPEG, or PNG image under 1 MB with a file name of 100 characters or fewer.
              </Tooltip>
            }
          >
            <label className="editProfileIcon">
              <FontAwesomeIcon icon={faPen} className="editProfileIconSVG"/>
              <PictureReader />
            </label>
          </OverlayTrigger>
        </div>
        <div className="text-center">
          <h3 className="font-weight-bold mt-3 mb-2">
            {userInfo?.profile.current_role
              ? UserAssignmentRoleDisplayedNames[userInfo.profile.current_role]
              : ""}
          </h3>
          <h4>{userInfo?.email}</h4>
        </div>

        <Form onSubmit={handleSubmit}>
          <FormGroup controlId="validation_first_name" className="my-3">
            <Form.Label className="font-weight-bold text-left">
              <FormattedMessage id="app.forms.firstName.label" />
              <span className="label-asterisk">*</span>
            </Form.Label>
              <FormControl
                required
                autoFocus
                className="fullWidthInput"
                name={"first_name"}
                type="string"
                value={firstName}
                onChange={handleFirstNameChange}
                placeholder="Enter"
              />
          </FormGroup>
          <FormGroup controlId="validation_last_name" className="my-3">
            <Form.Label className="font-weight-bold text-left">
              <FormattedMessage id="app.forms.lastName.label" />
              <span className="label-asterisk">*</span>
            </Form.Label>
              <FormControl
                required
                className="fullWidthInput"
                name={"last_name"}
                type="string"
                value={lastName}
                onChange={handleLastNameChange}
                placeholder="Enter"
              />
          </FormGroup>
          {/* <FormGroup controlId="validation_email" className="my-3">
            <Form.Label className="font-weight-bold text-left">
              <FormattedMessage id="app.forms.email.label" />
              <span className="label-asterisk">*</span>
            </Form.Label>
              <FormControl
                required
                className="fullWidthInput"
                name={"email"}
                type="email"
                value={email}
                disabled={true}
                onChange={handleEmailChange}
              />
          </FormGroup> */}
          {/* <FormGroup>
            <Form.Label className="font-weight-bold text-left">
              <FormattedMessage id="app.forms.accountType.label" />
            </Form.Label>
              <FormControl
                className="fullWidthInput"
                value={UserAssignmentRoleDisplayedNames[
                  userInfo?.profile.current_role!
                ]}
                onChange={handleCustomRoleTitleChange}
                disabled
              />
          </FormGroup> */}
           {!isDistrictOwner 
          ? 
            <>
              <FormGroup>
                <Form.Label className="font-weight-bold text-left">
                  <FormattedMessage id="app.forms.schoolRole.label" />
                </Form.Label>
                  <FormControl
                    className="fullWidthInput"
                    value={customRoleTitle ?? ""}
                    onChange={handleCustomRoleTitleChange}
                    placeholder="Enter (Optional)"
                  />
              </FormGroup>
              <FormGroup>
                <Form.Label className="font-weight-bold text-left">
                  <FormattedMessage id="app.dashboard.profile.modal.questionAboutSchools" />
                  <span className="label-asterisk">*</span>
                </Form.Label>
                
                <Select
                  isMulti
                  isClearable
                  isLoading={isLoading.getSchoolsList}
                  placeholder="Select"
                  value={schools.filter((school) =>
                    chosenSchools.includes(school.id)
                  )}
                  options={schools}
                  getOptionLabel={(school: School) => school.name}
                  getOptionValue={(school: School) => school.id.toString()}
                  onChange={handleSelectedSchoolChange}
                />
              </FormGroup>
            </>
          : null}
          <div className="mt-4 pt-2"></div>
          <hr />
            <div className="btnActions">
            <button type="button" className="whiteBtnSm" onClick={handleHideModal}>
              <FormattedMessage id="app.forms.buttons.cancel" />
             </button>
            <button className="blueBtnSm" type="submit"  disabled={!firstName || !lastName || (!isDistrictOwner && chosenSchools.length === 0)}>
              <FormattedMessage id="app.forms.buttons.save" />
              {isLoading.updateProfile && (
                <Spinner animation="border" size="sm" className="ml-1" />
              )}
            </button>
            </div>
        </Form>
      </Modal.Body>
    </Modal>
  );
};

const mapStateToProps = ({
  auth,
  onboarding,
}: ApplicationState): PropsFromState => {
  return {
    userInfo: auth.userInfo,
    schools: onboarding.schools,
    isLoading: {
      updateProfile: auth.isLoading.updateProfile,
      uploadUserProfileImage: auth.isLoading.uploadUserProfileImage,
      getSchoolsList: onboarding.isLoading.getSchoolsList,
    },
    errors: {
      getSchoolsList: onboarding.errors.getSchoolsList,
      updateProfile: auth.errors.updateProfile,
    },
    showModal: auth.modalsState.editProfileModal,
  };
};

const mapDispatchToProps = (dispatch: Dispatch): DispatchProps =>
  bindActionCreators(
    {
      hideEditProfileModal: hideEditProfileModal,
      getSchools: getSchools,
      updateProfile: updateProfile,
    },
    dispatch
  );

export default connect(mapStateToProps, mapDispatchToProps)(EditProfileModal);